import styled from '@emotion/styled';

import type { TableColumnWidth } from '~/ui/core/table/Table';
import {
    TableBody as BaseTableBody,
    TableCell as BaseTableCell,
    TableContainer as BaseTableContainer,
    TableHeader as BaseTableHeader,
    TableHeaderCell as BaseTableHeaderCell,
    TableRow as BaseTableRow,
    TableGrid,
} from '~/ui/core/table/Table';

import {
    ContentContainer as BaseContentContainer,
    Header as BaseHeader,
} from '../../../components/settings-page-styles';

export { Description, HeaderTop, Title, TitleGroup } from '../../../components/settings-page-styles';

export const ContentContainer = styled(BaseContentContainer)({
    overflow: 'unset',
});

export const Header = styled(BaseHeader)(({ theme }) => ({
    backgroundColor: theme.colors.white[200],
    paddingBottom: theme.spacing.s,
    position: 'sticky',
    top: 0,
    zIndex: 10,
}));

export const COLUMN_WIDTHS: ReadonlyArray<TableColumnWidth> = [
    '48px',
    'minmax(200px, 1.5fr)',
    'minmax(120px, 0.8fr)',
    'minmax(80px, 0.5fr)',
    'minmax(100px, 0.6fr)',
    'minmax(120px, 0.7fr)',
    '60px',
];

export const TableToolbar = styled.div(({ theme }) => ({
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing.l,
    justifyContent: 'space-between',
    marginBottom: theme.spacing.l,
}));

export const SearchContainer = styled.div(({ theme }) => ({
    '& input': {
        border: 'none',
        flex: 1,
        outline: 'none',
        padding: 0,
    },
    alignItems: 'center',
    backgroundColor: theme.colors.white[200],
    border: `1px solid ${theme.colors.gray[300]}`,
    borderRadius: theme.borderRadius.l,
    display: 'flex',
    gap: theme.spacing.s,
    maxWidth: '20rem',
    padding: `${theme.spacing.s} ${theme.spacing.l}`,
    width: '100%',
}));

export const SearchIcon = styled.div(({ theme }) => ({
    alignItems: 'center',
    color: theme.colors.gray[500],
    display: 'flex',
}));

export const TableContainer = BaseTableContainer;

export const Grid = TableGrid;

export const GridHeader = styled(BaseTableHeader)(({ theme }) => ({
    gridTemplateColumns: COLUMN_WIDTHS.join(' '),
    padding: theme.spacing.s,
}));

export const GridHeaderCell = styled(BaseTableHeaderCell)(({ theme }) => ({
    '&[data-column="checkbox"]': {
        paddingLeft: theme.spacing.xs,
    },
}));

export const GridBody = styled(BaseTableBody)({
    maxHeight: 'calc(100vh - 300px)',
    overflowY: 'auto',
});

export const GridRow = styled(BaseTableRow)(({ theme }) => ({
    '&:hover': {
        backgroundColor: theme.colors.gray[50],
    },
    borderBottom: `1px solid ${theme.colors.gray[200]}`,
    gridTemplateColumns: COLUMN_WIDTHS.join(' '),
}));

export const GridCell = styled(BaseTableCell)(({ theme }) => ({
    '&[data-column="checkbox"]': {
        paddingLeft: theme.spacing.l,
    },
}));

export const UserCellContainer = styled.div(({ theme }) => ({
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing.l,
}));

export const NumberedAvatar = styled.div(({ theme }) => ({
    ...theme.typography.body.XS.semibold,
    alignItems: 'center',
    backgroundColor: theme.colors.gray[200],
    borderRadius: '50%',
    color: theme.colors.gray[700],
    display: 'flex',
    flexShrink: 0,
    height: '2rem',
    justifyContent: 'center',
    width: '2rem',
}));

export const UserInfoCell = styled.div(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.xs,
}));

export const UserName = styled.span(({ theme }) => ({
    ...theme.typography.body.S.medium,
    color: theme.colors.gray[900],
}));

export const UserEmail = styled.span(({ theme }) => ({
    ...theme.typography.body.XS.regular,
    color: theme.colors.gray[600],
}));

export const DialogForm = styled.form({
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    minHeight: 0,
});

export const FormField = styled.div(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.m,
}));

export const FormLabel = styled.label(({ theme }) => ({
    ...theme.typography.body.S.semibold,
    color: theme.colors.gray[900],
}));

export const DialogActions = styled.div(({ theme }) => ({
    display: 'flex',
    gap: theme.spacing.m,
    justifyContent: 'flex-end',
    marginTop: theme.spacing.xl,
}));

export const UserInfoInDialog = styled.div(({ theme }) => ({
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing.l,
    marginBottom: theme.spacing.xl,
}));

export const FormRow = styled.div(({ theme }) => ({
    display: 'grid',
    gap: theme.spacing.l,
    gridTemplateColumns: '1fr 1fr',
}));
